import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import SupportModal from "@/components/SupportModal";
import { config as configApi } from "@/lib/api";
import {
  ArrowRight,
  Sparkles,
  Wand2,
  Layers,
  Camera,
  Download,
  Check,
  Music,
  Film,
  Building2,
  Palette,
  MoveRight,
} from "lucide-react";

const SAMPLE_DECKS = [
  {
    tag: "Music Artist",
    title: "Obsidian Editorial",
    accent: "#E50914",
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtdXNpYyUyMGFydGlzdCUyMGNvbmNlcnQlMjBzdGFnZXxlbnwwfHx8fDE3ODU1MzA1OTl8MA&ixlib=rb-4.1.0&q=85",
    lines: ["EPK", "Streaming", "Tour Reach", "Contact"],
  },
  {
    tag: "Actor / Model",
    title: "Chrome Cinema",
    accent: "#C0C0C0",
    img: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtb2RlbCUyMHBvcnRyYWl0JTIwY2luZW1hdGljJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg1NTMwNTk5fDA&ixlib=rb-4.1.0&q=85",
    lines: ["Headshots", "Filmography", "Editorial", "Reel"],
  },
  {
    tag: "Filmmaker",
    title: "Neon Manifesto",
    accent: "#FF2A2D",
    img: "https://images.unsplash.com/photo-1745860565625-c6f027b902b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxmaWxtJTIwc2xhdGUlMjBjaW5lbWF8ZW58MHx8fHwxNzg1NTMwNjEwfDA&ixlib=rb-4.1.0&q=85",
    lines: ["Reel", "Awards", "Stills", "Distribution"],
  },
  {
    tag: "Entrepreneur",
    title: "Boardroom Bloom",
    accent: "#F5F5F5",
    img: "https://images.unsplash.com/photo-1559223694-98ed5e272fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxwcmVzZW50YXRpb24lMjBzdGFnZSUyMGVudHJlcHJlbmV1cnxlbnwwfHx8fDE3ODU1MzA2MTB8MA&ixlib=rb-4.1.0&q=85",
    lines: ["Vision", "Traction", "Team", "Ask"],
  },
];

const FEATURES = [
  { icon: Wand2, title: "AI Creative Director", body: "Generates three completely different pitch-deck concepts from your brief — typography, palette, layout and graphic style." },
  { icon: Camera, title: "Media-First Layouts", body: "Your uploaded photos anchor every page. AI adds cinematic backgrounds, chrome accents and grain textures around them." },
  { icon: Layers, title: "Smart Page Modules", body: "Bio, discography, filmography, gallery, socials, contact and 20+ reusable blocks the AI arranges intelligently." },
  { icon: Palette, title: "Auto Palette & Fonts", body: "Colors extracted from your logo and photos. Typography paired to match your creator category and vibe." },
  { icon: Sparkles, title: "Tone-Perfect Copy", body: "Investor, label, casting, brand — rewrite everything in one click without touching your facts." },
  { icon: Download, title: "Instant Premium PDF", body: "Presentation-ready in seconds. PowerPoint, Keynote and hosted decks coming next." },
];

const FAQ = [
  { q: "Do I need a design background?", a: "Zero. Fill in a few fields, drop your photos, and KlickDeck's AI creative director handles typography, layout, palette and graphics for you." },
  { q: "Can I generate a deck without signing up?", a: "Yes — anonymous generation works end-to-end. Sign up with email + phone to save projects to your dashboard for revisions." },
  { q: "How are pages priced?", a: "$2.99 for 1 page, $9.99 for 5 pages, $17.99 for 10 pages. Additional pages are $1.99 each. Revisions after generation are $1.99." },
  { q: "What file format do I get?", a: "A high-quality PDF sized for pitch decks. PowerPoint, Keynote, PNG pages and interactive web decks are on the roadmap." },
  { q: "Who owns my deck?", a: "You do. You keep full commercial rights. We simply require you confirm the media you upload is yours or licensed." },
];

const CREATOR_STRIP = [
  { icon: Music, label: "Music" },
  { icon: Film, label: "Film" },
  { icon: Camera, label: "Photo" },
  { icon: Building2, label: "Business" },
  { icon: Palette, label: "Brand" },
];

export default function Landing() {
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [supportEmail, setSupportEmail] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    configApi.get().then((c) => { setPricing(c.pricing); setSupportEmail(c.support_email || ""); }).catch(() => {});
  }, []);

  const p = pricing || { "1": 2.99, "5": 9.99, "10": 17.99, additional: 1.99, revision: 1.99, premium_graphics: 4.99 };

  return (
    <div className="relative min-h-screen text-white">
      <Nav />
      {/* HERO */}
      <section className="relative pt-16 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="kd-tag mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={12} className="text-[#E50914]" /> AI Pitch Deck · Built for Creators
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl kd-display tracking-tighter leading-[0.95]"
          >
            Cinema-grade pitch decks,{" "}
            <span className="kd-serif kd-chrome-text">in a few clicks.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed"
          >
            KlickDeck turns your bio, photos and links into an investor-ready,
            label-ready, casting-ready pitch deck — designed like a magazine,
            not a template.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => nav("/create")}
              data-testid="hero-cta"
              className="group rounded-full px-7 py-4 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_44px_rgba(229,9,20,0.55)] hover:scale-[1.02] flex items-center gap-3"
            >
              Generate my deck
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="#samples"
              className="rounded-full px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-zinc-200 transition-colors flex items-center gap-2"
              data-testid="hero-samples"
            >
              See sample decks <MoveRight size={14} />
            </a>
          </motion.div>

          {/* Creator strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-16 flex flex-wrap items-center gap-6 text-zinc-500 text-xs uppercase tracking-[0.25em]"
          >
            <span>Built for</span>
            {CREATOR_STRIP.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <c.icon size={14} />
                {c.label}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Floating chrome orb */}
        <div className="pointer-events-none absolute right-[6%] top-[38%] w-[420px] h-[420px] rounded-full opacity-40 blur-[80px] bg-[radial-gradient(circle_at_30%_30%,#E50914_0%,transparent_60%)] kd-float" />
      </section>

      {/* SAMPLES */}
      <section id="samples" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="kd-tag mb-4">Sample decks</div>
              <h2 className="kd-display text-3xl md:text-5xl tracking-tight max-w-xl">
                Four creators. Four aesthetics. One workflow.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md">
              Every deck is styled around the creator, not a template. Below are
              four concept directions our AI generated live from real briefs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SAMPLE_DECKS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`sample-deck-${i}`}
                className="group relative rounded-3xl overflow-hidden aspect-[3/4] cursor-pointer"
              >
                <img
                  src={s.img}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
                <div className="absolute inset-0 p-5 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="kd-tag !bg-black/40 !border-white/20">{s.tag}</span>
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: s.accent, boxShadow: `0 0 12px ${s.accent}` }}
                    />
                  </div>
                  <div>
                    <div className="kd-display text-2xl mb-3">{s.title}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.lines.map((ln) => (
                        <span key={ln} className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full bg-white/10 border border-white/10 text-zinc-200">
                          {ln}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <div className="kd-tag mb-4">Features</div>
            <h2 className="kd-display text-3xl md:text-5xl tracking-tight max-w-2xl">
              Everything a design studio does — <span className="kd-serif kd-chrome-text">in one click.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`feature-${i}`}
                className="rounded-3xl p-8 bg-white/[0.02] border border-white/[0.06] hover:border-white/15 hover:bg-white/[0.04] transition-colors"
              >
                <div className="w-11 h-11 rounded-2xl bg-[#E50914]/10 border border-[#E50914]/25 flex items-center justify-center mb-5">
                  <f.icon size={18} className="text-[#FF2A2D]" />
                </div>
                <h3 className="kd-display text-xl mb-2 tracking-tight">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <div className="kd-tag mb-4">Pricing</div>
            <h2 className="kd-display text-3xl md:text-5xl tracking-tight max-w-2xl">
              Pay only for what you generate.
            </h2>
            <p className="mt-4 text-zinc-400 max-w-lg">No subscriptions. No design fees. Just per-deck pricing.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { pages: 1, price: p["1"], key: "deck_1_page", note: "Perfect for a single cover / one-pager" },
              { pages: 5, price: p["5"], key: "deck_5_pages", note: "Most popular — mini EPK", featured: true },
              { pages: 10, price: p["10"], key: "deck_10_pages", note: "Full electronic press kit" },
            ].map((tier, i) => (
              <motion.div
                key={tier.pages}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`pricing-tier-${tier.pages}`}
                className={`relative rounded-3xl p-8 ${tier.featured ? "kd-panel kd-red-glow" : "bg-white/[0.02] border border-white/[0.06]"}`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-8 kd-tag !bg-[#E50914] !border-[#E50914] !text-white">
                    Most Popular
                  </div>
                )}
                <div className="text-zinc-500 text-xs tracking-[0.2em] uppercase mb-3">{tier.pages} Page{tier.pages > 1 ? "s" : ""}</div>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="kd-display text-5xl">${tier.price}</span>
                </div>
                <p className="text-sm text-zinc-400 mb-6">{tier.note}</p>
                <ul className="space-y-2.5 mb-8 text-sm text-zinc-300">
                  <li className="flex gap-2 items-center"><Check size={14} className="text-[#FF2A2D]" /> AI design concepts</li>
                  <li className="flex gap-2 items-center"><Check size={14} className="text-[#FF2A2D]" /> Auto layouts & palette</li>
                  <li className="flex gap-2 items-center"><Check size={14} className="text-[#FF2A2D]" /> High-quality PDF</li>
                  <li className="flex gap-2 items-center"><Check size={14} className="text-[#FF2A2D]" /> Tone-perfect copy rewrite</li>
                </ul>
                <button
                  onClick={() => nav("/create")}
                  data-testid={`pricing-cta-${tier.pages}`}
                  className={`w-full rounded-full py-3 font-medium transition-all duration-300 ${
                    tier.featured
                      ? "bg-[#E50914] hover:bg-[#FF2A2D] text-white hover:shadow-[0_0_28px_rgba(229,9,20,0.5)]"
                      : "bg-white/10 hover:bg-white/15 text-white border border-white/10"
                  }`}
                >
                  Start with {tier.pages} page{tier.pages > 1 ? "s" : ""}
                </button>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-5 text-sm">
            {[
              { label: "Additional Page", value: `$${p.additional}`, sub: "each, added at checkout" },
              { label: "Revision", value: `$${p.revision}`, sub: "regenerate with new instructions" },
              { label: "Premium Graphics", value: `$${p.premium_graphics}`, sub: "AI-crafted backgrounds & textures" },
            ].map((it) => (
              <div key={it.label} className="rounded-2xl p-5 bg-white/[0.02] border border-white/[0.06] flex items-center justify-between">
                <div>
                  <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase">{it.label}</div>
                  <div className="text-white/90 text-lg mt-1">{it.value}</div>
                </div>
                <div className="text-zinc-500 text-xs max-w-[40%] text-right">{it.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="kd-tag mb-4">FAQ</div>
          <h2 className="kd-display text-3xl md:text-5xl tracking-tight mb-10">Answers, before you ask.</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors overflow-hidden"
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="text-white/90 font-medium">{f.q}</span>
                  <span className="text-zinc-400 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <div className="px-6 pb-5 text-zinc-400 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
          <div className="mt-16 kd-panel p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="kd-display text-3xl md:text-4xl tracking-tight max-w-lg">Your next opportunity deserves a deck.</h3>
              <p className="text-zinc-400 mt-3 max-w-md">One brief in. One cinematic pitch deck out. Zero design fees.</p>
            </div>
            <button
              onClick={() => nav("/create")}
              data-testid="footer-cta"
              className="rounded-full px-8 py-4 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_36px_rgba(229,9,20,0.55)] hover:scale-[1.02] flex items-center gap-2"
            >
              Generate my deck <ArrowRight size={18} />
            </button>
          </div>
          <footer className="mt-20 pb-10 flex flex-wrap items-center justify-between gap-4 text-zinc-500 text-xs">
            <div>© {new Date().getFullYear()} KlickDeck · Cinema for creators.</div>
            <div className="flex gap-5 items-center">
              <button type="button" onClick={() => setSupportOpen(true)} data-testid="footer-support" className="hover:text-white transition-colors">Contact</button>
              {supportEmail && (
                <a href={`mailto:${supportEmail}`} className="hover:text-white transition-colors">{supportEmail}</a>
              )}
              <Link to="/auth" className="hover:text-white transition-colors">Sign in</Link>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            </div>
          </footer>
        </div>
      </section>
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} supportEmail={supportEmail} />
    </div>
  );
}
